// Helpers for app data rows: summaries, labels and the data-type suggestions.

/** The `data_type` values the backend currently carries (free text is still allowed). */
export const KNOWN_DATA_TYPES = [
  "json",
  "action",
  "activityResponse",
  "appMetadata",
  "appdata.addressed",
  "appdata.field",
  "appdata.item",
  "currentLesson",
  "currentLessonStartTime",
  "lessonProgress",
  "mentor_profile",
  "onboarding_config",
  "onboarding_question",
  "path",
  "userContentItemReactions",
  "userMetadata",
  "userResponse",
  "vault",
  "vault_item",
  "waiting-for-activities",
];

/**
 * Normalise the backend's pagination block.
 *
 * Every list endpoint returns `pagination` with `has_next` / `has_previous`; navigation must use
 * those rather than comparing `count` to the page size.
 */
export const normalizePagination = (result = {}, fallback = {}) => {
  const block = result?.pagination ?? {};
  const total = Number(block.total ?? result?.total ?? 0);
  const pageSize = Number(block.page_size ?? fallback.pageSize ?? 50) || 50;
  const page = Number(block.page ?? fallback.page ?? 1) || 1;
  const totalPages = Number(block.total_pages ?? Math.max(1, Math.ceil(total / pageSize)));

  return {
    page,
    pageSize,
    total,
    totalPages,
    count: Number(block.count ?? result?.count ?? 0),
    offset: Number(block.offset ?? (page - 1) * pageSize),
    hasNext: Boolean(block.has_next),
    hasPrevious: Boolean(block.has_previous),
    nextOffset: block.next_offset ?? null,
    previousOffset: block.previous_offset ?? null,
  };
};

/** One line describing a row's `data`, for tables. */
export const summarizeAppData = (data) => {
  if (data === null || data === undefined) return "—";

  if (Array.isArray(data)) {
    return `[${data.length} item${data.length === 1 ? "" : "s"}]`;
  }

  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (keys.length === 0) return "{empty}";
    return `{${keys.slice(0, 4).join(", ")}${keys.length > 4 ? ", …" : ""}}`;
  }

  const text = String(data);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
};

/** Pretty JSON text for the JSON view. */
export const formatAppData = (data) => {
  if (data === undefined) return "null";

  try {
    return JSON.stringify(data, null, 2);
  } catch (error) {
    return String(data);
  }
};

/** A row's display label. */
export const rowLabel = (row) => `${row?.name ?? "?"}/${row?.key ?? "?"}`;

/**
 * The admin endpoint that returns one row.
 *
 * Global rows have a name/key path; user rows only expose the generic row endpoint.
 *
 * @param {object} params
 * @param {string} [params.baseUrl]
 * @param {"global"|"user"} [params.scope]
 * @param {string} [params.name]
 * @param {string} [params.key]
 * @param {string} [params.publicId]
 * @param {string|null} [params.clientId]
 */
export const appDataRowEndpoint = ({
  baseUrl = "",
  scope = "global",
  name = null,
  key = null,
  publicId = null,
  clientId = null,
} = {}) => {
  const client = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";

  if (scope === "global" && name && key) {
    return `${baseUrl}/admin/appdata/global/${encodeURIComponent(name)}/${encodeURIComponent(key)}${client}`;
  }

  if (publicId) {
    return `${baseUrl}/admin/appdata/rows/${encodeURIComponent(publicId)}${client}`;
  }

  return null;
};

/** The admin endpoint that returns every row of a global group. */
export const appDataGroupEndpoint = ({
  baseUrl = "",
  name = null,
  clientId = null,
  limit = null,
  offset = null,
} = {}) => {
  if (!name) return null;

  const params = new URLSearchParams();
  params.set("name", name);
  if (clientId) params.set("client_id", clientId);
  if (limit !== null) params.set("limit", String(limit));
  if (offset !== null) params.set("offset", String(offset));

  return `${baseUrl}/admin/appdata/global?${params.toString()}`;
};

const sortValue = (row, key) => {
  switch (key) {
    case "updated_at": {
      const time = Date.parse(row?.updated_at ?? "");
      return Number.isNaN(time) ? 0 : time;
    }
    case "locked":
      return row?.locked ? 1 : 0;
    case "scope":
      return String(row?.scope ?? "");
    case "user_email":
      return String(row?.user?.email ?? "");
    case "user":
      return String(
        row?.user?.email ?? [row?.user?.firstname, row?.user?.lastname].filter(Boolean).join(" ") ?? ""
      );
    case "data":
      return JSON.stringify(row?.data ?? "");
    default:
      return String(row?.[key] ?? "");
  }
};

/**
 * Sort app data rows by a column (client-side).
 *
 * @param {object[]} rows
 * @param {string} [sortKey] `name`, `key`, `data_type`, `locked`, `scope`, `user`, `data` or `updated_at`.
 * @param {"asc"|"desc"} [sortDir]
 */
export const sortRows = (rows = [], sortKey = "updated_at", sortDir = "desc") => {
  const list = [...(rows ?? [])];

  list.sort((a, b) => {
    const left = sortValue(a, sortKey);
    const right = sortValue(b, sortKey);

    const comparison =
      typeof left === "number" && typeof right === "number"
        ? left - right
        : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });

    return sortDir === "asc" ? comparison : -comparison;
  });

  return list;
};
